<template>
  <div id="billingCenter">
    <b-navbar type="light" variant="light" class="border-bottom">
      <b-navbar-brand href="/"><img src="/img/th-globe.jpg" width="28" height="28" class="rounded-circle mr-2" alt="">Trail<strong>HUB</strong></b-navbar-brand>
      <b-navbar-nav class="ml-auto">
        <b-nav-item href="/all">My trail systems</b-nav-item>
        <b-nav-item href="/docs/account-settings/billing/" target="_blank">Help</b-nav-item>
      </b-navbar-nav>
    </b-navbar>

    <div class="container py-4" style="max-width: 760px">
      <div v-if="!authReady" class="text-center text-muted py-5"><i class="fas fa-sync fa-spin"></i> Loading…</div>

      <div v-else-if="!currentUser" class="text-center py-5">
        <h2>Billing</h2>
        <p class="lead">Log in with your trail manager account to see your plan and payment details.</p>
        <b-btn variant="primary" href="/login/default/billing">Log in</b-btn>
      </div>

      <div v-else>
        <b-alert :show="checkoutResult === 'success'" variant="success" dismissible @dismissed="checkoutResult = null">
          <strong>Thanks, your plan is active.</strong> It can take up to a minute for your trail systems to update.
        </b-alert>
        <b-alert :show="checkoutResult === 'cancel'" variant="secondary" dismissible @dismissed="checkoutResult = null">Checkout was cancelled. Nothing was charged.</b-alert>
        <b-alert :show="checkoutResult === 'scheduled'" variant="success" dismissible @dismissed="checkoutResult = null"><strong>You're booked.</strong> Your card is saved and your plan starts on the date you chose. We'll email you a reminder a week before.</b-alert>
        <b-alert :show="!!notice" variant="success" dismissible @dismissed="notice = ''">{{ notice }}</b-alert>
        <b-alert :show="!!error" variant="danger" dismissible @dismissed="error = ''">{{ error }}</b-alert>

        <p v-if="!overviewLoaded" class="text-center text-muted py-5"><i class="fas fa-sync fa-spin"></i> Loading your plan…</p>

        <!-- ============ HOME: where do I stand? ============ -->
        <template v-else-if="view === 'home'">
          <h2 class="mb-3">Billing</h2>

          <b-card no-body class="status-card mb-4" :class="'status-' + state">
            <b-card-body>
              <div class="d-flex align-items-start">
                <div class="status-icon mr-3"><i class="fas" :class="stateIcon"></i></div>
                <div class="flex-grow-1">
                  <!-- Payment failed -->
                  <template v-if="state === 'pastDue'">
                    <h4>Your last payment didn't go through</h4>
                    <p>Update your card to keep your trail maps online. Your {{ planLine(current) }} plan is otherwise unchanged.</p>
                    <div class="actions">
                      <b-btn variant="danger" :disabled="portalLoading" @click="openPortal()">Update card</b-btn>
                    </div>
                  </template>

                  <!-- Paused for the season -->
                  <template v-else-if="state === 'paused'">
                    <h4>Paused for the season</h4>
                    <p>Your <strong>{{ planLine(current) }}</strong> plan restarts automatically on <strong>{{ fmtDate(current.pausedUntil) }}</strong>. You won't be charged before then.</p>
                    <ul class="facts">
                      <li>Visitors see "Closed for the season, reopening {{ fmtDate(current.pausedUntil) }}".</li>
                      <li>You can still log in and get your trails ready.</li>
                      <li>We'll email you a reminder a week before it restarts.</li>
                    </ul>
                    <div class="actions">
                      <b-btn variant="primary" @click="go('changeDate', current.pausedUntil)">Change restart date</b-btn>
                      <b-btn variant="outline-secondary" :disabled="busy" @click="confirmResume()">Reopen now</b-btn>
                    </div>
                  </template>

                  <!-- Booked to start later -->
                  <template v-else-if="state === 'scheduled'">
                    <h4>You're booked for {{ fmtDate(current.scheduledStart) }}</h4>
                    <p>Your <strong>{{ planLine(current) }}</strong> plan starts on <strong>{{ fmtDate(current.scheduledStart) }}</strong>. Your card is saved and won't be charged before then.</p>
                    <ul class="facts">
                      <li>Your public map goes live on {{ fmtDate(current.scheduledStart) }}.</li>
                      <li>Until then visitors see "Closed for the season". You can still edit your trails.</li>
                    </ul>
                    <div class="actions">
                      <b-btn variant="primary" @click="go('changeDate', current.scheduledStart)">Change start date</b-btn>
                      <b-btn variant="outline-secondary" :disabled="busy" @click="confirmStartNow()">Open today instead</b-btn>
                      <b-btn variant="link" class="text-danger" :disabled="busy" @click="confirmCancelScheduled()">Cancel booking</b-btn>
                    </div>
                  </template>

                  <!-- Active -->
                  <template v-else-if="state === 'active'">
                    <h4>{{ tierName(current.role) }} plan<span v-if="current.amount" class="text-muted font-weight-normal"> · {{ money(current) }}</span></h4>
                    <p v-if="current.cancelAtPeriodEnd">Your plan is set to end on <strong>{{ fmtDate(current.currentPeriodEnd) }}</strong>. After that your public maps go offline.</p>
                    <p v-else>Active<span v-if="current.currentPeriodEnd">. Next payment on <strong>{{ fmtDate(current.currentPeriodEnd) }}</strong></span>.</p>
                    <p class="small text-muted">Covers up to {{ tierLimit(current.role) }} trail systems · you own {{ ownedSystems.length }}.</p>
                    <div class="actions">
                      <b-btn v-if="canPause" variant="primary" @click="go('pause', nextSeasonStart())"><i class="fas fa-pause mr-1"></i> Pause for the off-season</b-btn>
                      <b-btn v-if="current.cancelAtPeriodEnd" variant="primary" :disabled="portalLoading" @click="openPortal()">Keep my plan</b-btn>
                      <b-btn variant="outline-secondary" @click="go('plans')">Change plan</b-btn>
                    </div>
                    <p v-if="current.interval === 'year'" class="small text-muted mt-2 mb-0">Annual plans are paid for the whole year, so there's nothing to pause.</p>
                  </template>

                  <!-- Returning customer -->
                  <template v-else-if="state === 'returning'">
                    <h4>Welcome back</h4>
                    <p>Your last plan was <strong>{{ planLine(lastPlan) }}</strong>. Book it again for the season. Your card is only charged on the day you open.</p>
                    <div class="actions">
                      <b-btn variant="primary" @click="go('restart')">Book my season</b-btn>
                      <b-btn variant="outline-secondary" @click="go('plans')">Pick a different plan</b-btn>
                    </div>
                  </template>

                  <!-- Brand new -->
                  <template v-else>
                    <h4>No plan yet</h4>
                    <p>New trail systems get a free 30-day trial. Choose a plan to keep your public maps online after that. One plan covers every trail system you own.</p>
                    <div class="actions">
                      <b-btn variant="primary" @click="go('plans')">Choose a plan</b-btn>
                    </div>
                  </template>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Trail systems -->
          <h6 class="text-uppercase text-muted small mb-2">Your trail systems</h6>
          <p v-if="ownedSystems.length === 0" class="text-muted">You don't own any trail systems yet. <a href="/all">Create one</a> to start a free trial.</p>
          <b-list-group class="mb-4">
            <b-list-group-item v-for="ts in ownedSystems" :key="ts.id" :id="'ts-' + ts.id" :class="{ highlight: ts.id === focusId }">
              <div class="d-flex flex-wrap align-items-center justify-content-between">
                <div class="mr-3">
                  <a :href="'/ts/' + ts.id"><strong>{{ ts.name }}</strong></a>
                  <b-badge :variant="describe(ts).variant" class="ml-2 align-middle">{{ describe(ts).label }}</b-badge>
                  <div v-if="showSystemDetail(ts)" class="small text-muted mt-1">{{ describe(ts).detail }}</div>
                </div>
                <b-btn v-if="describe(ts).action === 'manage'" size="sm" variant="danger" @click="openPortal()">Update card</b-btn>
              </div>
            </b-list-group-item>
          </b-list-group>

          <!-- Secondary links -->
          <div class="more-links small">
            <a v-if="hasStripeCustomer" href="#" @click.prevent="openPortal()"><i class="fas fa-credit-card mr-1"></i>Card, receipts &amp; cancellation</a>
            <a href="/docs/account-settings/seasonal-pause/" target="_blank"><i class="fas fa-snowflake mr-1"></i>How seasonal pausing works</a>
            <a href="/contact/"><i class="fas fa-envelope mr-1"></i>Questions? Contact us</a>
          </div>
        </template>

        <!-- ============ PAUSE ============ -->
        <task-panel v-else-if="view === 'pause'" title="Pause for the off-season" @back="go('home')">
          <p>Stop billing while you're closed. Your plan restarts by itself on the date you pick.</p>
          <date-step v-model="date" label="When do you reopen?" :min="minDate" :max="maxDate" :suggestions="seasonSuggestions"></date-step>
          <summary-box v-if="date">
            <li><strong>Today:</strong> billing stops. Visitors see "Closed for the season, reopening {{ fmtDay(date) }}".</li>
            <li><strong>While paused:</strong> you can still log in and edit your trails. Reopen early any time.</li>
            <li><strong>A week before:</strong> we email you a reminder.</li>
            <li><strong>{{ fmtDay(date) }}:</strong> your {{ planLine(current) }} plan restarts on the card on file and your map goes live.</li>
          </summary-box>
          <template #actions>
            <b-btn variant="primary" :disabled="busy || !date" @click="doPause()"><i v-if="busy" class="fas fa-sync fa-spin mr-1"></i>Pause until {{ date ? fmtDay(date) : '…' }}</b-btn>
          </template>
        </task-panel>

        <!-- ============ CHANGE DATE ============ -->
        <task-panel v-else-if="view === 'changeDate'" :title="state === 'paused' ? 'Change your restart date' : 'Change your start date'" @back="go('home')">
          <date-step v-model="date" label="New date" :min="minDate" :max="maxDate" :suggestions="seasonSuggestions"></date-step>
          <summary-box v-if="date">
            <li>Your {{ planLine(current) }} plan starts on <strong>{{ fmtDay(date) }}</strong>. No charges before then.</li>
            <li>Your public map goes live that day.</li>
          </summary-box>
          <template #actions>
            <b-btn variant="primary" :disabled="busy || !date" @click="doChangeDate()"><i v-if="busy" class="fas fa-sync fa-spin mr-1"></i>Save new date</b-btn>
          </template>
        </task-panel>

        <!-- ============ RESTART LAST PLAN ============ -->
        <task-panel v-else-if="view === 'restart'" title="Book your season" @back="go('home')">
          <div class="chosen-plan mb-3">
            <div class="text-muted small">Plan</div>
            <div class="h5 mb-0">{{ planLine(lastPlan) }}</div>
            <a href="#" class="small" @click.prevent="go('plans')">Pick a different plan</a>
          </div>
          <start-step v-model="start" :min="minDate" :max="maxDate" :suggestions="seasonSuggestions"></start-step>
          <summary-box v-if="startReady">
            <li><strong>Next screen:</strong> enter or confirm your card on Stripe's secure page.</li>
            <li v-if="start.mode === 'later'"><strong>{{ fmtDay(start.date) }}:</strong> first charge of {{ money(lastPlan) || 'your plan price' }}, and your map goes live.</li>
            <li v-else><strong>Today:</strong> first charge of {{ money(lastPlan) || 'your plan price' }}, and your map goes live.</li>
            <li>Pause again any time from this page when your season ends.</li>
          </summary-box>
          <template #actions>
            <b-btn variant="primary" :disabled="busy || !startReady" @click="startPlan(lastPlan.priceId)"><i v-if="busy" class="fas fa-sync fa-spin mr-1"></i>Continue to payment</b-btn>
          </template>
        </task-panel>

        <!-- ============ PLANS ============ -->
        <task-panel v-else-if="view === 'plans'" :title="selectedPrice ? 'When should it start?' : (current ? 'Change plan' : 'Choose a plan')" @back="selectedPrice ? (selectedPrice = null) : go('home')">
          <template v-if="!selectedPrice">
            <div class="d-flex flex-wrap mb-3">
              <b-form-radio-group v-model="priceMode" buttons button-variant="outline-primary" size="sm" class="mr-3 mb-2"
                :options="[{ text: 'Business', value: 'forProfit' }, { text: 'Nonprofit', value: 'nonProfit' }]"></b-form-radio-group>
              <b-form-radio-group v-model="pricingInterval" buttons button-variant="outline-primary" size="sm" class="mb-2"
                :options="[{ text: 'Monthly', value: 'month' }, { text: 'Yearly (save ~15%)', value: 'year' }]"></b-form-radio-group>
            </div>
            <p v-if="pricingInterval === 'month'" class="small text-muted">Monthly plans can be paused during your off-season.</p>
            <p v-if="plansLoading" class="text-muted"><i class="fas fa-sync fa-spin"></i> Loading plans…</p>
            <b-list-group class="mb-3">
              <b-list-group-item v-for="plan in orderedPlans" :key="plan.id" class="plan-row">
                <div class="d-flex flex-wrap align-items-center justify-content-between">
                  <div class="mr-3">
                    <strong>{{ plan.name }}</strong>
                    <b-badge v-if="current && current.role === plan.role" variant="success" class="ml-1">Your plan</b-badge>
                    <div class="small text-muted">Up to {{ tierLimit(plan.role) }} trail systems<span v-if="plan.description"> · {{ plan.description }}</span></div>
                  </div>
                  <div class="text-right" v-for="price in pricesFor(plan)" :key="price.id">
                    <span class="h5 mb-0 mr-2">${{ (price.unit_amount / 100).toLocaleString() }}<small class="text-muted">/{{ price.interval }}</small></span>
                    <b-btn v-if="current && current.role === plan.role" size="sm" variant="outline-success" disabled>Current</b-btn>
                    <b-btn v-else-if="current" size="sm" variant="outline-primary" :disabled="portalLoading" @click="openPortal()">Switch</b-btn>
                    <b-btn v-else size="sm" variant="primary" @click="pickPrice(plan, price)">Select</b-btn>
                  </div>
                </div>
              </b-list-group-item>
            </b-list-group>
            <p class="small text-muted mb-0">
              Every plan includes trail status, conditions, snow reports, notices, embeds, QR codes and subscriber alerts. <a href="/#pricing" target="_blank">Compare plans</a>.
              <span v-if="current">Plan switches open Stripe's billing page, where you'll see the price difference before confirming.</span>
            </p>
          </template>

          <template v-else>
            <div class="chosen-plan mb-3">
              <div class="text-muted small">Plan</div>
              <div class="h5 mb-0">{{ selectedPlan.name }} · ${{ (selectedPrice.unit_amount / 100).toLocaleString() }}/{{ selectedPrice.interval }}</div>
            </div>
            <start-step v-model="start" :min="minDate" :max="maxDate" :suggestions="seasonSuggestions"></start-step>
            <summary-box v-if="startReady">
              <li><strong>Next screen:</strong> enter your card on Stripe's secure page.</li>
              <li><strong>{{ start.mode === 'later' ? fmtDay(start.date) : 'Today' }}:</strong> first charge, and your public map stays or goes live.</li>
            </summary-box>
          </template>

          <template v-if="selectedPrice" #actions>
            <b-btn variant="primary" :disabled="busy || !startReady" @click="startPlan(selectedPrice.id)"><i v-if="busy" class="fas fa-sync fa-spin mr-1"></i>Continue to payment</b-btn>
          </template>
        </task-panel>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase'
import { describeBilling, fmtDate, tierName } from '../../assets/lib/billingStatus'
import TaskPanel from './parts/TaskPanel.vue'
import SummaryBox from './parts/SummaryBox.vue'
import DateStep from './parts/DateStep.vue'
import StartStep from './parts/StartStep.vue'
const fb = require('../../firebaseConfig.js')

const TIER_LIMITS = { square: 2, diamond: 4, doubleDiamond: 8, circle: 2, triangle: 4, star: 8 }
const DAY = 86400000

export default {
  components: { TaskPanel, SummaryBox, DateStep, StartStep },
  data () {
    return {
      authReady: false,
      currentUser: null,
      ownedSystems: [],
      hasStripeCustomer: false,
      plansLoading: false,
      pricingInterval: 'month',
      priceMode: 'forProfit',
      portalLoading: false,
      error: '',
      notice: '',
      focusId: this.$route.query.ts || null,
      checkoutResult: this.$route.query.checkout || null,
      overview: null,
      overviewLoaded: false,
      busy: false,
      view: 'home',
      date: '',
      start: { mode: 'later', date: '' },
      selectedPrice: null,
      selectedPlan: null,
      unsubscribe: []
    }
  },
  computed: {
    current () { return this.overview && this.overview.current },
    lastPlan () { return this.overview && this.overview.last },
    // The one situation the page is about.
    state () {
      const c = this.current
      if (c && c.status === 'past_due') return 'pastDue'
      if (c && c.pausedUntil) return 'paused'
      if (c && c.scheduledStart) return 'scheduled'
      if (c) return 'active'
      if (this.lastPlan && this.lastPlan.priceId) return 'returning'
      return 'none'
    },
    stateIcon () {
      return { pastDue: 'fa-exclamation-triangle', paused: 'fa-snowflake', scheduled: 'fa-calendar-check', active: 'fa-check-circle', returning: 'fa-hand-sparkles', none: 'fa-map' }[this.state]
    },
    canPause () { return this.current && this.current.interval === 'month' && !this.current.cancelAtPeriodEnd },
    minDate () { return this.isoDay(new Date(Date.now() + 2 * DAY)) },
    maxDate () { return this.isoDay(new Date(Date.now() + 330 * DAY)) },
    startReady () { return this.start.mode === 'now' || Boolean(this.start.date) },
    seasonSuggestions () {
      // Common opening dates, nearest first, within the allowed window.
      const out = []
      const now = new Date()
      for (const [m, d] of [[10, 15], [11, 1], [11, 15], [4, 1]]) {
        let t = new Date(Date.UTC(now.getUTCFullYear(), m, d, 14))
        if (t.getTime() < Date.now() + 2 * DAY) t = new Date(Date.UTC(now.getUTCFullYear() + 1, m, d, 14))
        if (t.getTime() <= Date.now() + 330 * DAY) out.push(t)
      }
      return out.sort((a, b) => a - b).slice(0, 3).map(t => ({ value: this.isoDay(t), text: t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }))
    },
    orderedPlans () {
      const order = this.priceMode === 'nonProfit' ? ['circle', 'triangle', 'star'] : ['square', 'diamond', 'doubleDiamond']
      return order.map(role => this.$store.state.plans.find(p => p.role === role)).filter(Boolean)
    }
  },
  mounted () {
    fb.auth.onAuthStateChanged(user => {
      this.currentUser = user
      this.authReady = true
      if (user) this.load(user)
    })
  },
  beforeDestroy () {
    this.unsubscribe.forEach(fn => fn())
  },
  methods: {
    describe: describeBilling,
    fmtDate,
    tierName,
    tierLimit (role) { return TIER_LIMITS[role] || '?' },
    isoDay (d) { return d.toISOString().slice(0, 10) },
    fmtDay (iso) { return fmtDate(iso + 'T14:00:00Z') },
    money (row) {
      if (!row || typeof row.amount !== 'number') return ''
      const amt = row.amount / 100
      return '$' + (Number.isInteger(amt) ? amt : amt.toFixed(2)) + (row.interval ? '/' + row.interval : '')
    },
    planLine (row) {
      if (!row) return ''
      return tierName(row.role) + (row.amount ? ' (' + this.money(row) + ')' : '')
    },
    pricesFor (plan) { return (plan.prices || []).filter(p => p.interval === this.pricingInterval) },
    // Per-system detail only when it says something the status card doesn't.
    showSystemDetail (ts) {
      const s = describeBilling(ts).status
      return !['active', 'seasonal', 'override'].includes(s)
    },
    // Default restart date: the next December 1st at least 2 days out.
    nextSeasonStart () {
      const now = new Date()
      let d = new Date(Date.UTC(now.getUTCFullYear(), 11, 1))
      if (d.getTime() < Date.now() + 2 * DAY) d = new Date(Date.UTC(now.getUTCFullYear() + 1, 11, 1))
      return d.toISOString()
    },
    go (view, iso) {
      this.error = ''
      this.view = view
      if (view === 'pause' || view === 'changeDate') this.date = iso ? String(iso).slice(0, 10) : ''
      if (view === 'restart' || view === 'plans') this.start = { mode: 'later', date: this.nextSeasonStart().slice(0, 10) }
      if (view === 'plans') {
        this.selectedPrice = null
        if (this.current) { this.priceMode = this.isNonprofit(this.current.role) ? 'nonProfit' : 'forProfit'; this.pricingInterval = this.current.interval || 'month' } else if (this.lastPlan) { this.priceMode = this.isNonprofit(this.lastPlan.role) ? 'nonProfit' : 'forProfit' }
      }
      window.scrollTo(0, 0)
    },
    isNonprofit (role) { return ['circle', 'triangle', 'star'].includes(role) },
    pickPrice (plan, price) {
      this.selectedPlan = plan
      this.selectedPrice = price
      this.start = { mode: 'later', date: this.nextSeasonStart().slice(0, 10) }
    },
    callSeasonal (action, payload) {
      const fn = firebase.app().functions('us-central1').httpsCallable('seasonalBilling')
      return fn({ action, payload }).then(r => r.data)
    },
    async loadOverview () {
      try {
        this.overview = await this.callSeasonal('overview', {})
      } catch (err) {
        console.log('overview', err.message)
      }
      this.overviewLoaded = true
    },
    async seasonal (action, payload, message) {
      this.busy = true
      this.error = ''
      try {
        await this.callSeasonal(action, payload)
        this.notice = message
        await this.loadOverview()
        this.view = 'home'
        window.scrollTo(0, 0)
      } catch (err) {
        this.error = err.message
      }
      this.busy = false
    },
    confirm (title, body, ok, variant = 'primary') {
      return this.$bvModal.msgBoxConfirm(body, { title, okTitle: ok, okVariant: variant, cancelTitle: 'Not now', centered: true })
    },
    doPause () {
      return this.seasonal('pause', { subscriptionId: this.current.id, resumeOn: this.date }, `You're paused. Your plan restarts on ${this.fmtDay(this.date)} and we'll remind you a week before.`)
    },
    doChangeDate () {
      return this.seasonal('changeDate', { subscriptionId: this.current.id, date: this.date }, `Date updated to ${this.fmtDay(this.date)}.`)
    },
    async confirmResume () {
      if (await this.confirm('Reopen now?', `Your public map goes live right away and regular ${this.money(this.current) || ''} billing picks up again.`, 'Reopen now')) {
        this.seasonal('resume', { subscriptionId: this.current.id }, 'Welcome back! Your plan is active again.')
      }
    },
    async confirmStartNow () {
      if (await this.confirm('Open today?', `Your plan starts now and your card is charged ${this.money(this.current) || 'the plan price'} today.`, 'Start today')) {
        this.seasonal('startNow', { subscriptionId: this.current.id }, 'Your plan has started and your map is live.')
      }
    },
    async confirmCancelScheduled () {
      if (await this.confirm('Cancel this booking?', "Your plan won't start and nothing will be charged. You can book again any time.", 'Cancel booking', 'danger')) {
        this.seasonal('cancelScheduled', { subscriptionId: this.current.id }, 'Booking cancelled. Nothing was charged.')
      }
    },
    async startPlan (priceId) {
      this.busy = true
      this.error = ''
      try {
        const startOn = this.start.mode === 'later' ? this.start.date : null
        const { url } = await this.callSeasonal('scheduleStart', { priceId, startOn })
        window.location.assign(url)
      } catch (err) {
        this.error = err.message
        this.busy = false
      }
    },
    async load (user) {
      this.plansLoading = true
      this.$store.dispatch('fetchPlans').catch(err => { this.error = 'Could not load plans: ' + err.message }).then(() => { this.plansLoading = false })
      // Live updates so the page reflects Stripe and the billing engine without a reload.
      this.unsubscribe.push(fb.trailSystemsCollection.where('ownerId', '==', user.uid).orderBy('createdOn', 'desc').onSnapshot(snap => {
        this.ownedSystems = snap.docs.map(d => Object.assign({ id: d.id }, d.data()))
        if (this.focusId) this.$nextTick(() => { const el = document.getElementById('ts-' + this.focusId); if (el) el.scrollIntoView({ block: 'center' }) })
      }, err => { this.error = err.message }))
      this.unsubscribe.push(fb.customersCollection.doc(user.uid).collection('subscriptions').onSnapshot(() => {
        this.loadOverview()
      }, err => { console.log(err.message) }))
      this.unsubscribe.push(fb.customersCollection.doc(user.uid).onSnapshot(snap => {
        this.hasStripeCustomer = snap.exists && Boolean(snap.data().stripeId)
      }, err => { console.log(err.message) }))
    },
    async openPortal () {
      this.portalLoading = true
      try {
        const fn = firebase.app().functions('us-central1').httpsCallable('ext-firestore-stripe-subscriptions-createPortalLink')
        const { data } = await fn({ returnUrl: window.location.origin + '/billing' })
        window.location.assign(data.url)
      } catch (err) {
        this.portalLoading = false
        this.error = 'Could not open the billing page: ' + err.message
      }
    }
  }
}
</script>

<style>
/* Not scoped: the parts/ components share these styles. */
#billingCenter { min-height: 100vh; background: #f4f8fa; }
#billingCenter .highlight { box-shadow: 0 0 0 3px rgba(75, 176, 220, .5); }
#billingCenter .status-card { border-left: 5px solid #28a745; }
#billingCenter .status-card h4 { margin-bottom: .5rem; }
#billingCenter .status-pastDue { border-left-color: #dc3545; }
#billingCenter .status-paused, #billingCenter .status-scheduled { border-left-color: #4bb0dc; }
#billingCenter .status-returning { border-left-color: #4bb0dc; background: #f4fafd; }
#billingCenter .status-none { border-left-color: #6c757d; }
#billingCenter .status-icon { font-size: 1.75rem; width: 2rem; text-align: center; color: #28a745; }
#billingCenter .status-pastDue .status-icon { color: #dc3545; }
#billingCenter .status-paused .status-icon, #billingCenter .status-scheduled .status-icon, #billingCenter .status-returning .status-icon { color: #4bb0dc; }
#billingCenter .status-none .status-icon { color: #6c757d; }
#billingCenter .facts { padding-left: 1.1rem; color: #555; font-size: .9rem; }
#billingCenter .actions .btn { margin: 0 .4rem .4rem 0; }
#billingCenter .more-links a { display: inline-block; margin: 0 1.25rem .5rem 0; color: #555; }
#billingCenter .summary-box { background: #f4f8fa; border-radius: 6px; padding: 12px 16px; }
#billingCenter .summary-box ul { padding-left: 1.1rem; }
#billingCenter .summary-box li { margin-bottom: .25rem; }
#billingCenter .chosen-plan { background: #f4f8fa; border-radius: 6px; padding: 10px 14px; }
#billingCenter .start-choice { border: 1px solid #dfe7ec; border-radius: 6px; padding: 10px 14px; margin-bottom: 8px; cursor: pointer; }
#billingCenter .start-choice.on { border-color: #4bb0dc; background: #f4fafd; }
#billingCenter .plan-row .h5 { vertical-align: middle; }
</style>
